import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useGameStore } from '../../store/gameStore';

interface NumberPadProps {
  onNumberClick?: (num: number) => void;
}

export default function NumberPad({ onNumberClick }: NumberPadProps) {
  const { puzzle, currentState, selectedCell, setNumber, isNoteMode } = useGameStore();

  // Determine grid configuration from puzzle length
  const gridConfig = useMemo(() => {
    const totalCells = puzzle.length;
    if (totalCells === 16) {
      return { size: 4, boxSize: 2, maxNumber: 4 };
    }
    return { size: 9, boxSize: 3, maxNumber: 9 };
  }, [puzzle.length]);

  const { size, boxSize, maxNumber } = gridConfig;

  const handleClick = (num: number) => {
    if (onNumberClick) {
      onNumberClick(num);
    } else {
      setNumber(num);
    }
  };

  // Count occurrences of each number
  const numberCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    for (let i = 1; i <= maxNumber; i++) {
      counts[i] = currentState.split('').filter((c) => c === String(i)).length;
    }
    return counts;
  }, [currentState, maxNumber]);

  // Calculate available numbers for the selected cell
  // For 4x4 (beginner): All numbers are always available to keep the game simple
  // For 9x9: Only filter when a SINGLE constraint leaves exactly one number
  const availableNumbers = useMemo(() => {
    const allNumbers = Array.from({ length: maxNumber }, (_, i) => i + 1);
    
    // For 4x4 beginner mode: always show all numbers as available
    if (maxNumber === 4) {
      return new Set(allNumbers);
    }
    
    // If no cell selected or cell is already filled, all numbers are available
    if (selectedCell === null || currentState[selectedCell] !== '0') {
      return new Set(allNumbers);
    }

    const row = Math.floor(selectedCell / size);
    const col = selectedCell % size;
    const boxRow = Math.floor(row / boxSize) * boxSize;
    const boxCol = Math.floor(col / boxSize) * boxSize;

    // Collect numbers used in the same row
    const rowUsed = new Set<number>();
    for (let c = 0; c < size; c++) {
      const val = parseInt(currentState[row * size + c]);
      if (val > 0) rowUsed.add(val);
    }
    const rowCandidates = allNumbers.filter((n) => !rowUsed.has(n));

    // Collect numbers used in the same column
    const colUsed = new Set<number>();
    for (let r = 0; r < size; r++) {
      const val = parseInt(currentState[r * size + col]);
      if (val > 0) colUsed.add(val);
    }
    const colCandidates = allNumbers.filter((n) => !colUsed.has(n));

    // Collect numbers used in the same box
    const boxUsed = new Set<number>();
    for (let r = boxRow; r < boxRow + boxSize; r++) {
      for (let c = boxCol; c < boxCol + boxSize; c++) {
        const val = parseInt(currentState[r * size + c]);
        if (val > 0) boxUsed.add(val);
      }
    }
    const boxCandidates = allNumbers.filter((n) => !boxUsed.has(n));

    // Only filter if ONE of the constraints leaves exactly one candidate
    // This is single-step deduction that's obvious to the player
    if (rowCandidates.length === 1) {
      return new Set(rowCandidates);
    }
    if (colCandidates.length === 1) {
      return new Set(colCandidates);
    }
    if (boxCandidates.length === 1) {
      return new Set(boxCandidates);
    }

    // Otherwise, all numbers are available - let the player figure it out
    return new Set(allNumbers);
  }, [selectedCell, currentState, size, boxSize, maxNumber]);

  const numbers = Array.from({ length: maxNumber }, (_, i) => i + 1);
  const padClassName = maxNumber === 4 ? 'number-pad number-pad-4 w-full' : 'number-pad w-full';

  return (
    <div className={padClassName}>
      {numbers.map((num) => {
        const count = numberCounts[num];
        const remaining = size - count; // Remaining squares that can be filled with this number
        const isComplete = count >= size;
        const isAvailable = availableNumbers.has(num);
        // For 4x4: only disable when number is complete
        // For 9x9: disable if complete OR not available for current cell (unless in note mode)
        const isDisabled = maxNumber === 4 
          ? isComplete 
          : (isComplete || (!isNoteMode && !isAvailable));

        return (
          <motion.button
            key={num}
            className={`number-btn touch-manipulation ${isDisabled ? 'disabled' : ''} ${isNoteMode ? 'ring-2 ring-green-500/50' : ''
              }`}
            onClick={() => handleClick(num)}
            disabled={isDisabled}
            whileTap={!isDisabled ? { scale: 0.95 } : {}}
          >
            <span className="relative">
              {num}
              {remaining > 0 && remaining < size && (
                <span className="absolute -top-1.5 sm:-top-2 -right-2 sm:-right-3 text-[0.5rem] sm:text-[0.6rem] text-green-600">
                  {remaining}
                </span>
              )}
            </span>
          </motion.button>
        );
      })}
    </div>
  );
}
